<script setup lang="ts">
const props = defineProps<{
  controller: ReturnType<typeof useModal>;
}>();

const {
  cartItems,
  totalPrice,
  shippingTotal,
  isEmpty,
  shippingCosts,
  subtotal,
} = useCart();

const localePath = useLocalePath();
const { formatLink } = useInternationalization(localePath);

const freeShippingThreshold = computed(() => {
  // Find the free shipping price rule (gross: 0)
  return (
    shippingCosts.value[0]?.shippingMethod?.prices?.find((price) =>
      price.currencyPrice.some((cp) => cp.gross === 0)
    )?.quantityStart || 0
  );
});

const remainingAmount = computed(() => {
  return freeShippingThreshold.value
    ? (Math.max(0, Math.round((freeShippingThreshold.value - subtotal.value) * 100) / 100)).toFixed(2)
    : "0.00";
});

</script>

<template>
  <LayoutSidebar :controller="props.controller" side="right">
    <aside class="flex-1 bg-white overflow-y-auto">
      <div
        class="flex justify-between items-center mb-12 py-2 border-b border-solid border-cust_green md:border-0 sticky top-0 z-50 bg-white px-5 md:px-8"
      >
        <div
          class="closeCart cursor-pointer text-cust_gray text-3xl"
          data-testid="cart-close-button"
          @click="props.controller.close"
        >
          <i class="fa-solid fa-xmark"></i>
        </div>
        <div class="flex items-center gap-4">
          <h4 class="m-0 text-26px md:text-32px">{{ $t("cart.header") }}</h4>
          <span
            v-if="!isEmpty"
            class="cart-num w-10 h-10 rounded-lg bg-cust_cream flex items-center justify-center"
            >{{ cartItems.length }}</span
          >
        </div>
      </div>

      <div class="mt-8 px-5 md:px-8 mb-12">
        <div class="flow-root">
          <ul
            v-if="!isEmpty"
            role="list"
            class="-my-6 px-0 divide-secondary-200"
          >
            <li
              v-for="cartItem in cartItems"
              :key="cartItem.id"
              class="flex py-4"
            >
              <CheckoutCartItem :cart-item="cartItem" :is-flyout="true" />
            </li>
          </ul>
          <div v-else class="text-2xl text-center">
            {{ $t("cart.emptyCartLabel") }}
          </div>
        </div>
      </div>

      <div class="px-5 md:px-8">
        <div
          v-if="remainingAmount !== '0.00'"
          class="flex border border-solid border-[#4AD494] p-3 rounded-2xl mb-6 md:mb-6 gap-4"
        >
          <span class="w-6 aspect-square mt-1">
            <img src="assets/images/i-icon.png" width="24" height="24" alt="" />
          </span>
          <p class="w-[calc(100%-45px)] text-base md:text-xl text-left">
            {{ $t("cart.messages.offerText", { count: remainingAmount }) }}
          </p>
        </div>
      </div>
    </aside>

    <aside aria-label="Side Cart Links" class="bg-white py-6 pt-4 px-5 md:px-8 pb-0 border-0 border-t border-[#e5e7eb] border-t-secondary-200">
      <div class="pt-0 pb-0 md:pt-0">
        
        <div
          class=" border-solid border-cust_green font-redit_sans_con text-17px md:text-xl"
        >
          <div
            class="border-b border-solid border-cust_green flex items-center justify-between py-2 md:py-2"
          >
            <p>
              <strong class="font-extrabold">{{ $t("cart.subtotal") }}</strong>
            </p>
            <SharedPrice :value="totalPrice" data-testid="cart-subtotal" class="font-extrabold"/>
          </div>
          <div
            class="border-b border-solid border-cust_green flex items-center justify-between py-2 md:py-2"
          >
            <p>{{ $t("cart.shippingCosts") }}</p>
            <SharedPrice
              :value="shippingTotal"
              class="text-secondary-900"
              data-testid="cart-shipping-cost"
            >
              <template #afterPrice>
                <span>*</span>
              </template>
            </SharedPrice>
          </div>
        </div>
        <div class="mt-4 text-left flex flex-col gap-3 md:gap-3">
          <p class="text-base">{{ $t("cart.taxInfo") }}</p>
          <!-- <div class="flex flex-wrap justify-between">
            <CheckoutPromotionCode />
          </div> -->
          <NuxtLink
            class="btn-primary gap-4 h-12 md:h-16 w-full rounded-[30px] bg-cust_green"
            :class="{
              '!text-[#bcc1c7] bg-[#f9f9f9] cursor-default': isEmpty,
              'text-white bg-primary hover:bg-dark': !isEmpty,
            }"
            :to="formatLink(isEmpty ? '' : '/checkout')"
            @click="props.controller.close"
            data-testid="cart-checkout-link"
          >
            {{ $t("cart.checkout") }} <i class="fa-solid fa-angle-right"></i>
          </NuxtLink>
          <div class="text-center">
            <!-- <a href="#" class="underline">Warenkorb anzeigen</a> -->
            <NuxtLink
              class="underline"
              :to="formatLink(`/checkout/cart`)"
              data-testid="cart-checkout-shopping-cart"
              @click="props.controller.close"
            >
              {{ $t("plubel.checkout.viewShoppingCart") }}
            </NuxtLink>
          </div>
        </div>
      </div>
    </aside>
  </LayoutSidebar>
</template>
